import { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'

import logo from '../assets/reilm-logo.svg'
import { useAuth } from '../features/auth/AuthContext'

const EYE_OFF_PATH =
  'M3 3l18 18M10.5 6.2A10.8 10.8 0 0112 6c7 0 10 6 10 6a17.6 17.6 0 01-3.2 4M6.2 7.6A17.4 17.4 0 002 12s3 6 10 6c1.6 0 3-.3 4.3-.8'

const EyeIcon = ({ open }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    {open ? (
      <>
        <path
          d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7z"
          stroke="#7d8c74"
          strokeWidth="1.5"
        />
        <circle cx="12" cy="12" r="3" fill="#7d8c74" />
      </>
    ) : (
      <path
        d={EYE_OFF_PATH}
        stroke="#7d8c74"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    )}
  </svg>
)

const Login = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { login } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const redirectTo = location.state?.from ?? '/'

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await login({ email, password })
      navigate(redirectTo, { replace: true })
    } catch (err) {
      setError(err.message || 'Unable to sign in')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-brand-greenSec flex flex-col">
      <header className="h-[91px] bg-brand-greenSec border-b border-brand-muted">
        <div className="h-full max-w-canvas mx-auto px-[120px] flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src={logo} alt="reilm" className="w-[148px] h-[43px]" />
          </Link>

          <div className="flex items-center gap-2">
            <span className="text-[16px] font-medium text-brand-dark">
              Don't have an account?
            </span>
            <Link
              to="/signup"
              className="h-[40px] px-6 inline-flex items-center justify-center rounded-full border border-brand-green text-brand-dark text-[14px] font-semibold"
            >
              Start 7-Day Free Trial
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6">
        <form
          onSubmit={handleSubmit}
          className="w-[416px] flex flex-col items-center gap-8"
        >
          <h1 className="text-[40px] leading-[48px] font-extrabold text-brand-dark text-center w-full">
            Welcome Back!
          </h1>

          <div className="w-full flex flex-col gap-6">
            <div className="flex flex-col gap-2 items-end w-full">
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email Address"
                className="w-full h-[48px] bg-white border border-[#cbdcc3] rounded-full px-6 text-[14px] text-brand-dark placeholder:text-[#909090] outline-none focus:border-brand-green"
              />

              <div className="relative w-full">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full h-[48px] bg-white border border-[#cbdcc3] rounded-full pl-6 pr-12 text-[14px] text-brand-dark placeholder:text-[#909090] outline-none focus:border-brand-green"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  className="absolute right-4 top-1/2 -translate-y-1/2"
                >
                  <EyeIcon open={showPassword} />
                </button>
              </div>

              <Link
                to="/forgot-password"
                className="text-[14px] font-medium text-brand-green"
              >
                Forgot password?
              </Link>
            </div>

            {error && (
              <p className="text-[14px] text-red-600 font-medium text-center">
                {error}
              </p>
            )}

            <div className="w-full flex justify-center">
              <button
                type="submit"
                disabled={submitting}
                className="w-[196px] h-[56px] rounded-full bg-brand-green text-white text-[16px] font-semibold disabled:opacity-60"
              >
                {submitting ? 'Signing in…' : 'Continue'}
              </button>
            </div>
          </div>
        </form>
      </main>
    </div>
  )
}

export default Login
